import { ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/utilities';

/**
 * Sticky ATC mobile OTWS.
 * - Relais du formulaire principal : le clic soumet le formulaire de la fiche produit, avec la même variante,
 *   le même abonnement (selling plan), la même quantité et les mêmes ventes croisées.
 * - Affiché seulement quand le bouton d'ajout principal est hors de l'écran (et masqué quand le pied de page ou le panier est visible).
 * - Si aucun formulaire principal n'est trouvé, le sticky soumet son propre formulaire (variante + abonnement synchronisés).
 */
class OtwsStickyAtcMobile extends HTMLElement {
  constructor() {
    super();

    this.onVariantUpdated = this.onVariantUpdated.bind(this);
    this.onSubscriptionUpdated = this.onSubscriptionUpdated.bind(this);
    this.onFormUpdated = this.onFormUpdated.bind(this);
    this.onSubmitCapture = this.onSubmitCapture.bind(this);
    this.onCartUpdated = this.onCartUpdated.bind(this);
    this.updateVisibility = this.updateVisibility.bind(this);
    this.queueVisibilityUpdate = this.queueVisibilityUpdate.bind(this);

    this.frame = null;
  }

  connectedCallback() {
    this.variantInput = this.querySelector('input[name="id"]');
    this.sellingPlanInput = this.querySelector('input[name="selling_plan"]');
    this.addButton = this.querySelector('[data-ref="add-to-cart-button"]');
    this.addButtonContainer = this.querySelector('[data-ref="add-to-cart-button-container"]');
    this.buttonLabel = this.querySelector('[data-sticky-atc-button-label]');
    this.priceTargets = this.querySelectorAll('[data-sticky-atc-price]');
    this.compareAtPriceTargets = this.querySelectorAll('[data-sticky-atc-compare-price]');
    this.productImage = this.querySelector('[data-sticky-atc-image]');
    this.mainProductForm = this.findMainProductForm();

    document.addEventListener(ThemeEvents.variantUpdated, this.onVariantUpdated);
    document.addEventListener(ThemeEvents.subscriptionUpdated, this.onSubscriptionUpdated);
    document.addEventListener(ThemeEvents.formUpdated, this.onFormUpdated);
    document.addEventListener(ThemeEvents.cartUpdated, this.onCartUpdated);
    this.addEventListener('submit', this.onSubmitCapture, true);

    this.syncSellingPlan();
    this.observeVisibility();
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.variantUpdated, this.onVariantUpdated);
    document.removeEventListener(ThemeEvents.subscriptionUpdated, this.onSubscriptionUpdated);
    document.removeEventListener(ThemeEvents.formUpdated, this.onFormUpdated);
    document.removeEventListener(ThemeEvents.cartUpdated, this.onCartUpdated);
    this.removeEventListener('submit', this.onSubmitCapture, true);
    window.removeEventListener('scroll', this.queueVisibilityUpdate);
    window.removeEventListener('resize', this.queueVisibilityUpdate);
    this.drawerObserver?.disconnect();
    if (this.frame) window.cancelAnimationFrame(this.frame);
    window.clearTimeout(this.loadingTimeout);
  }

  /**
   * Formulaire produit de la section principale (hors sticky, hors tiroir panier et hors ajout rapide).
   * @returns {HTMLElement|null}
   */
  findMainProductForm() {
    const candidates = [
      ...document.querySelectorAll('[id$="__main"] product-form'),
      ...document.querySelectorAll('main product-form'),
    ];

    return (
      candidates.find(
        (form) =>
          !this.contains(form) &&
          !form.closest('cart-drawer, quick-add-modal, dialog') &&
          form.querySelector('form[action$="/cart/add"]')
      ) || null
    );
  }

  observeVisibility() {
    if (!this.mainProductForm) {
      this.dataset.active = 'true';
      return;
    }

    this.footer = document.querySelector('[data-ref="footer"]') || document.querySelector('footer');

    window.addEventListener('scroll', this.queueVisibilityUpdate, { passive: true });
    window.addEventListener('resize', this.queueVisibilityUpdate, { passive: true });

    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      this.drawerObserver = new MutationObserver(this.queueVisibilityUpdate);
      this.drawerObserver.observe(cartDrawer, { attributes: true, attributeFilter: ['class'] });
    }

    this.updateVisibility();
  }

  queueVisibilityUpdate() {
    if (this.frame) return;
    this.frame = window.requestAnimationFrame(() => {
      this.frame = null;
      this.updateVisibility();
    });
  }

  /**
   * Le bouton principal est relu à chaque fois : le thème le remplace quand la variante change.
   */
  updateVisibility() {
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const isInViewport = (element) => {
      if (!element) return false;
      const rect = element.getBoundingClientRect();
      return rect.bottom > 0 && rect.top < viewportHeight;
    };

    const mainButton =
      this.mainProductForm?.querySelector('[data-ref="add-to-cart-button"]') ||
      this.mainProductForm?.querySelector('[type="submit"]');
    const mainButtonVisible = isInViewport(mainButton);
    // Autres boutons d'achat de la page (ex. hero de landing) : le sticky reste masqué tant qu'ils sont visibles.
    const otherBuyButtonVisible = [...document.querySelectorAll('[data-sticky-atc-hide]')].some(isInViewport);
    const footerVisible = isInViewport(this.footer);
    const cartDrawerOpen = document.querySelector('cart-drawer.is-open') !== null;

    const active = !mainButtonVisible && !otherBuyButtonVisible && !footerVisible && !cartDrawerOpen;
    this.dataset.active = active ? 'true' : 'false';
    this.toggleAttribute('inert', !active);
  }

  /**
   * Le clic sur le sticky soumet le formulaire principal quand il existe et qu'il est disponible.
   * Écouteur en phase de capture : il passe avant le product-form du sticky.
   */
  onSubmitCapture(event) {
    const mainForm = this.mainProductForm?.querySelector('form[action$="/cart/add"]');
    const mainButton = this.mainProductForm?.querySelector('[data-ref="add-to-cart-button"]');
    if (!mainForm || !mainButton || mainButton.disabled) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    if (this.addButton?.classList.contains('is-loading')) return;
    this.addButton?.classList.add('is-loading');
    this.loadingTimeout = window.setTimeout(() => this.addButton?.classList.remove('is-loading'), 6000);

    if (typeof mainForm.requestSubmit === 'function') {
      mainForm.requestSubmit();
    } else {
      mainButton.click();
    }
  }

  onCartUpdated() {
    window.clearTimeout(this.loadingTimeout);
    this.addButton?.classList.remove('is-loading');
  }

  isEventForThisProduct(event) {
    const productId = event.detail?.data?.productId;
    if (!productId || !this.dataset.productId) return true;
    return String(productId) === String(this.dataset.productId);
  }

  onVariantUpdated(event) {
    if (!this.isEventForThisProduct(event)) return;
    if (this.mainProductForm && !this.mainProductForm.contains(event.target)) return;

    const variant = event.detail?.resource;
    if (!variant?.id) return;

    const isAvailable = variant.available !== false;

    if (this.variantInput) {
      this.variantInput.value = variant.id;
    }

    if (this.addButton) {
      this.addButton.disabled = !isAvailable;
    }

    if (this.buttonLabel) {
      this.buttonLabel.textContent = isAvailable ? this.dataset.buttonLabel : this.dataset.soldOutLabel;
    }

    if (this.addButtonContainer) {
      this.addButtonContainer.dataset.price = variant.price;
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        this.addButtonContainer.dataset.compareAtPrice = variant.compare_at_price;
      } else {
        delete this.addButtonContainer.dataset.compareAtPrice;
      }
    }

    // Avec un formulaire principal, le prix affiché vient de form:updated (abonnement et quantité inclus).
    if (!this.mainProductForm) {
      this.renderPrices(variant.price, variant.compare_at_price);
    }

    const imageUrl = variant.featured_media?.preview_image?.src;
    if (this.productImage && imageUrl) {
      this.productImage.removeAttribute('srcset');
      this.productImage.src = imageUrl;
    }

    this.syncSellingPlan();
  }

  onSubscriptionUpdated() {
    this.syncSellingPlan();
  }

  /**
   * Prix total calculé par le formulaire principal (variante, quantité, abonnement, ventes croisées).
   */
  onFormUpdated(event) {
    if (!this.mainProductForm || event.target !== this.mainProductForm) return;
    const prices = event.detail?.prices;
    if (!prices || typeof prices.price !== 'number' || Number.isNaN(prices.price)) return;
    this.renderPrices(prices.price, prices.compare_at_price);
  }

  syncSellingPlan() {
    if (!this.sellingPlanInput) return;
    const scope = this.mainProductForm || document;
    const subscription = scope.querySelector('subscription-component');
    const sellingPlan =
      subscription && typeof subscription.getSelectedSellingPlan === 'function'
        ? subscription.getSelectedSellingPlan()
        : '';
    this.sellingPlanInput.value = sellingPlan || '';
  }

  renderPrices(price, compareAtPrice) {
    this.priceTargets.forEach((priceTarget) => {
      priceTarget.textContent = this.formatMoney(price);
    });

    this.compareAtPriceTargets.forEach((compareAtPriceTarget) => {
      compareAtPriceTarget.textContent = compareAtPrice && compareAtPrice > price ? this.formatMoney(compareAtPrice) : '';
    });
  }

  formatMoney(cents) {
    return formatMoney(cents);
  }
}

if (!customElements.get('otws-sticky-atc-mobile')) {
  customElements.define('otws-sticky-atc-mobile', OtwsStickyAtcMobile);
}
